<script setup>
// Uma rede = um card = um botão (decisão Ivan 08/10). Conta na conexão única
// do Instagram vê só "Conectar Instagram" (a mesma conexão cria a caixa,
// publica, mede e responde comentários); as outras, só o login nativo.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import instagramClient from 'dashboard/api/channel/instagramClient';
import SocialConnect from '../components/SocialConnect.vue';

const props = defineProps({
  // Quanto esperar a caixa criada pela conexão (2 s × 15 = 30 s).
  pollIntervalMs: { type: Number, default: 2000 },
  pollAttempts: { type: Number, default: 15 },
});

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const { currentAccount } = useAccount();

const singleConnection = computed(
  () => !!currentAccount.value?.instagram_single_connection
);

const hasError = ref(false);
const errorStateMessage = ref('');
const errorStateDescription = ref('');
const isRequestingAuthorization = ref(false);
const isWaitingInbox = ref(false);
let knownInboxIds = new Set();

const inboxes = () => store.getters['inboxes/getInboxes'] || [];

onMounted(() => {
  knownInboxIds = new Set(inboxes().map(inbox => inbox.id));

  const urlParams = new URLSearchParams(window.location.search);
  const errorCode = urlParams.get('code');
  const errorMessage = urlParams.get('error_message');

  if (errorMessage) {
    hasError.value = true;
    if (errorCode === '400') {
      errorStateMessage.value = errorMessage;
      errorStateDescription.value = t('INBOX_MGMT.ADD.INSTAGRAM.ERROR_AUTH');
    } else {
      errorStateMessage.value = t('INBOX_MGMT.ADD.INSTAGRAM.ERROR_MESSAGE');
      errorStateDescription.value = errorMessage;
    }
  }
  // User need to remove the error params from the url to avoid the error to be shown again after page reload, so that user can try again
  const cleanURL = window.location.pathname;
  window.history.replaceState({}, document.title, cleanURL);
});

const requestAuthorization = async () => {
  isRequestingAuthorization.value = true;
  const {
    data: { url },
  } = await instagramClient.generateAuthorization();
  window.location.href = url;
};

const findProvisionedInbox = account =>
  inboxes().find(
    inbox =>
      inbox.instagram_single_connection &&
      (!knownInboxIds.has(inbox.id) ||
        (account?.username && inbox.name === `@${account.username}`))
  );

const sleep = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

// A caixa nasce no servidor quando a conexão chega; espera ela aparecer.
const onConnected = async account => {
  isWaitingInbox.value = true;
  for (let attempt = 0; attempt < props.pollAttempts; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    await store.dispatch('inboxes/get');
    const inbox = findProvisionedInbox(account);
    if (inbox) {
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: { page: 'new', inbox_id: inbox.id },
      });
      return;
    }
    // eslint-disable-next-line no-await-in-loop
    await sleep(props.pollIntervalMs);
  }
  isWaitingInbox.value = false;
  useAlert(t('INBOX_MGMT.ADD.INSTAGRAM.SINGLE_NOT_READY'));
  router.push({ name: 'settings_inbox_list' });
};
</script>

<template>
  <div class="h-full p-6 w-full max-w-full flex-shrink-0 flex-grow-0">
    <div class="flex flex-col items-center justify-start h-full text-center">
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5>{{ errorStateMessage }}</h5>
        <p
          v-if="errorStateDescription"
          v-dompurify-html="errorStateDescription"
        />
      </div>
      <div v-else-if="singleConnection" class="w-full max-w-xl">
        <SocialConnect
          platform="instagram"
          :description="t('INBOX_MGMT.ADD.INSTAGRAM.SINGLE_DESC')"
          @connected="onConnected"
        />
        <p v-if="isWaitingInbox" class="mt-3 text-xs text-n-slate-10">
          {{ t('INBOX_MGMT.ADD.INSTAGRAM.SINGLE_WAITING_INBOX') }}
        </p>
      </div>
      <div
        v-else
        class="flex flex-col items-center justify-center px-8 py-10 text-center rounded-2xl outline outline-1 outline-n-weak"
      >
        <h6 class="text-2xl font-medium">
          {{ t('INBOX_MGMT.ADD.INSTAGRAM.CONNECT_YOUR_INSTAGRAM_PROFILE') }}
        </h6>
        <p class="py-6 text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.ADD.INSTAGRAM.HELP') }}
        </p>
        <button
          class="flex items-center justify-center px-8 py-3.5 gap-2 text-white rounded-full bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] hover:shadow-lg transition-all duration-300 min-w-[240px] overflow-hidden"
          :disabled="isRequestingAuthorization"
          @click="requestAuthorization()"
        >
          <span class="i-ri-instagram-line size-5" />
          <span class="text-base font-medium">
            {{ t('INBOX_MGMT.ADD.INSTAGRAM.CONTINUE_WITH_INSTAGRAM') }}
          </span>
          <span v-if="isRequestingAuthorization" class="ml-2">
            <svg
              class="w-5 h-5 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </span>
        </button>
      </div>
    </div>
  </div>
</template>
